import { startOfWeek, format as formatDate } from "date-fns";
import type { CountryType } from "store/types";
import type { TargetingArea } from "../targeting/TargetingContext";
import { parseScheduleDate } from "./h2hSchedule";

// Per-city min/max distribution gates (epic T7). The minimum
// (`minimumMissionLimit`) and the weekly limit (`weeklyMissionLimit`) are
// server-driven per city, falling back to the country defaults — mirroring the
// legacy getCorrectSettingsObject precedence (use the city value when it is a
// real number, including 0; otherwise the country default).

type TargetingChannel = "H2H" | "D2D";

export interface CityLimits {
  /** Minimum distributors (H2H) / areas (D2D) required to proceed (AC31). */
  minimum: number;
  /** Per-week distributor cap (H2H) / ×12 = area cap (D2D) — 0 means uncapped. */
  weeklyMissionLimit: number;
}

export interface LimitMessage {
  title: string;
  message: string;
}

// D2D's campaign-wide area cap is the weekly limit across the default 12-week
// campaign window (legacy campaignMissionLimitD2D = weeklyMissionLimit * 12).
const D2D_WEEKS = 12;

const pickSetting = (cityValue: number | null | undefined, countryValue: number | null | undefined): number => {
  if (cityValue === 0 || (typeof cityValue === "number" && !Number.isNaN(cityValue))) return cityValue;
  return typeof countryValue === "number" && !Number.isNaN(countryValue) ? countryValue : 0;
};

export const resolveCityLimits = (country: CountryType | null, cityId: number | null): CityLimits => {
  const defaults = country?.countryDefaultSettings;
  const citySetting = (country?.citiesSettings ?? []).find((c: any) => String(c.id) === String(cityId));
  return {
    minimum: pickSetting(citySetting?.minimumMissionLimit, defaults?.minimumMissionLimit),
    weeklyMissionLimit: pickSetting(citySetting?.weeklyMissionLimit, defaults?.weeklyMissionLimit),
  };
};

// Maximum allowed count for the channel: H2H caps distributors per week; D2D
// caps total areas at the weekly limit across the 12-week window. 0/unset →
// Infinity (uncapped), matching legacy.
export const resolveMaximum = (channel: TargetingChannel, weeklyMissionLimit: number): number => {
  if (!weeklyMissionLimit) return Infinity;
  return channel === "D2D" ? weeklyMissionLimit * D2D_WEEKS : weeklyMissionLimit;
};

// AC31 — the disabled-CTA tooltip copy (exact legacy strings, kept verbatim so
// QA's expected text matches).
export const getMinLimitMessage = (channel: TargetingChannel, minimum: number): LimitMessage => {
  const noun = channel === "D2D" ? "areas" : "distributors";
  return {
    title: `Not enough ${noun}!`,
    message: `Please, select at least ${minimum} ${noun} to continue.`,
  };
};

// AC32 — the inline maximum error copy (exact legacy strings).
export const getMaxLimitMessage = (channel: TargetingChannel, maximum: number): LimitMessage =>
  channel === "D2D"
    ? {
        title: "Too many areas!",
        message: `The total number of areas should not exceed ${maximum}.`,
      }
    : {
        title: "Too many distributors per week!",
        message: `Please, select fewer distributors. The total number of distributors in any given week should not exceed ${maximum}.`,
      };

// Total distributors selected for H2H = sum of each area's auto-derived
// distributor count (pairs × BAs-per-location, persisted as `units`).
export const countDistributors = (areas: TargetingArea[]): number =>
  areas.reduce((sum, area) => sum + (area.units ?? 0), 0);

// The current count the minimum/maximum gates compare against: H2H counts
// distributors, D2D counts selected areas (mirrors the legacy missionCount).
export const countForGate = (channel: TargetingChannel, areas: TargetingArea[]): number =>
  channel === "D2D" ? areas.length : countDistributors(areas);

// The largest distributor total falling in any single ISO week across all
// areas' schedules (H2H weekly-max basis). Distributors per week = (date,time)
// pairs whose date lands in that week × BAs-per-location. Date-only rows (no
// time slots) contribute nothing.
export const maxWeeklyDistributors = (areas: TargetingArea[], baPerLocation: number): number => {
  const pairsByWeek = new Map<string, number>();
  areas.forEach((area) => {
    (area.schedule ?? []).forEach((entry) => {
      if (!entry.times.length) return;
      const day = parseScheduleDate(entry.date);
      if (!day) return; // skip rows with an unparseable date
      const weekKey = formatDate(startOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");
      pairsByWeek.set(weekKey, (pairsByWeek.get(weekKey) ?? 0) + entry.times.length);
    });
  });
  let max = 0;
  pairsByWeek.forEach((pairs) => {
    max = Math.max(max, pairs * baPerLocation);
  });
  return max;
};
